import { getCountryFormDefinition } from "CMS/utils";
import CompanyGoogleMap from "company/detail/components/CompanyGoogleMap";
import { FinancialRiskEdit } from "company/detail/components/FinancialRiskEdit";
import { PostalAndVisitingAddress } from "company/detail/components/models";
import { PostalAddressPreviewPanel } from "company/detail/components/PostalAddress";
import { PostalAddressForm } from "company/detail/components/PostalAddressForm";
import { VisitingAddressForm } from "company/detail/components/VisitingAddressForm";
import { convertToBankFormState, convertToFormState, getInstruction } from "company/detail/utils";
import { PreviewPanelDetailRow } from "components/Atoms/details/PreviewPanelDetailRow";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import { DeletedRow } from "components/DetailPage/DeletedRow";
import NotValidatedRow from "components/DetailPage/NotValidatedRow";
import PhoneRow from "components/DetailPage/PhoneRow";
import StatusDropdown from "components/dropdowns/reactselect/StatusDropdown";
import FinancialRiskCard from "components/FinancialRisk/FinancialRiskCard";
import { ViewOnMap } from "components/GoogleMap/ViewOnMap";
import { PreviewPanelInPageEditableField } from "components/MainSectionTemplate/InPageEditableField";
import { PreviewPanelSectionTemplate } from "components/MainSectionTemplate/PreviewPanelSectionTemplate";
import { convertToPhoneNumbers, PhoneInput } from "components/PhoneInput";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import Status from "components/Status/Status";
import { useHasAnyTags } from "components/Tag/hooks";
import TagList from "components/Tag/TagList";
import TagsSelect from "components/Tag/TagsSelect";
import UrlInput from "components/urlinput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import VatNumberDetails from "components/VatNumber/VatNumberDetails";
import WebsiteLink from "components/Website/WebsiteLink";
import React from "react";
import { isNullOrEmptyString } from "utilities";

interface CompanyPreviewPanelContentProps {
  company: server.dto.Company
  viewOnMap?: boolean

  onChange: () => Promise<void>
}

export const CompanyPreviewPanelContent = (props: CompanyPreviewPanelContentProps) => {
  const [companyFormState, setCompanyFormState] = React.useState(convertToFormState(props.company));
  const bankFormState = convertToBankFormState(props.company.bank);
  const [tags, setTags] = React.useState(props.company.tags || []);

  const countryFormDefinition = getCountryFormDefinition(props.company.admAddress.country);

  const onValueChanged = (key: string, value: any) => {
    if (key === "visitingAddress" || key === "postalAddress") {
      setCompanyFormState(prevState => ({
        ...prevState,
        address: value,
      }));
    } else if (key === "phone") {
      setCompanyFormState(prevState => ({
        ...prevState,
        phone: value,
      }));
    } else if (key === "website") {
      setCompanyFormState(prevState => ({
        ...prevState,
        website: value,
      }));
    } else if (key === "facebook") {
      setCompanyFormState(prevState => ({
        ...prevState,
        facebook: value,
      }));
    } else if (key === "euVatNumber") {
      setCompanyFormState(prevState => ({
        ...prevState,
        euVatNumber: value,
      }));
    } else if (key === "fiscalNumber1") {
      setCompanyFormState(prevState => ({
        ...prevState,
        fiscalNumber1: value,
      }));
    } else if (key === "fiscalNumber2") {
      setCompanyFormState(prevState => ({
        ...prevState,
        fiscalNumber2: value,
      }));
    } else if (key === "businessRegistrationNumber") {
      setCompanyFormState(prevState => ({
        ...prevState,
        businessRegistrationNumber: value,
      }));
    } else if (key === "internalId") {
      setCompanyFormState(prevState => ({
        ...prevState,
        internalId: value,
      }));
    } else if (key === "financialRisk") {
      setCompanyFormState(prevState => ({
        ...prevState,
        financialRisk: value,
      }));
    } else if (key === "status") {
      setCompanyFormState(prevState => ({
        ...prevState,
        status: value,
      }));
    } else if (key === "tags") {
      setTags(value);
    } else if (key === "owner") {
      setCompanyFormState(prevCompany => ({
        ...prevCompany,
        owner: value,
      }));
    }
  }

  const hasMultipleStatuses = site.currentEnvironment.statuses.company.length > 1;
  const hasAnyTags = useHasAnyTags("company");
  const hasLandlineNumbers = !isNullOrEmptyString(companyFormState.phone);
  const canInPageEdit = props.company.permissions.canModify;

  return <PreviewPanelSectionTemplate
    enabled={canInPageEdit}
    onSuccessfulUpdate={props.onChange}
    onValueChanged={onValueChanged}
  >
    <div className="flex-column gap-larger">
      {props.company.isDeleted && <PreviewPanelDetailRow>
        <DeletedRow />
      </PreviewPanelDetailRow>}
      {!props.company.isValidated && <PreviewPanelDetailRow>
        <NotValidatedRow
          canApprove={canInPageEdit}
          sublabel={RESX.Planning.InputFromWebform}
          approveEndpoint={() => window.CoreApi.Company.Validate({
            companyId: props.company.companyId
          })} />
      </PreviewPanelDetailRow>}

      <PreviewPanelDetailRow labelTop={RESX.Company.VisitingAddress}>
        <PreviewPanelInPageEditableField
          field={{
            key: "visitingAddress",
            render: () => <>
              <div>{props.company.name}</div>
              <div className="text-sublabel">{props.company.address.formatted.join(", ")}</div>
            </>,
            inPageEdit: {
              useCustomSavingState: true,
              fullWidth: true,
              render: ({ value, onChange, submit, isSaving }) => <VisitingAddressForm
                noLeftPadding
                submit={submit}
                onChange={onChange}
                value={value}
                isSaving={isSaving}
              />,
              initialValue: companyFormState.address,
              type: "custom",
              submitChange: (value: PostalAndVisitingAddress) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, {
                  regularName: value.visitingAddress.companyName,
                  regularAddress: {
                    addressLine1: value.visitingAddress.addressLine1,
                    addressLine2: value.visitingAddress.addressLine2,
                    city: value.visitingAddress.city,
                    postalCode: value.visitingAddress.postalCode,
                    stateId: value.visitingAddress.state.key,
                  },
                  administrativeName: value.postalAddress.companyName,
                  administrativeAddress: {
                    addressLine1: value.postalAddress.addressLine1,
                    addressLine2: value.postalAddress.addressLine2,
                    city: value.postalAddress.city,
                    postalCode: value.postalAddress.postalCode,
                    stateId: value.postalAddress.state.key,
                  }
                });

                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Company.PostalAddress}>
        <PreviewPanelInPageEditableField
          field={{
            key: "postalAddress",
            render: () => <PostalAddressPreviewPanel company={props.company} />,
            inPageEdit: {
              useCustomSavingState: true,
              fullWidth: true,
              render: ({ value, onChange, submit, isSaving }) => <PostalAddressForm
                noLeftPadding
                value={value}
                onChange={onChange}
                submit={submit}
                isSaving={isSaving}
              />,
              initialValue: companyFormState.address,
              type: "custom",
              submitChange: (value: PostalAndVisitingAddress) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, {
                  regularName: value.visitingAddress.companyName,
                  regularAddress: {
                    addressLine1: value.visitingAddress.addressLine1,
                    addressLine2: value.visitingAddress.addressLine2,
                    city: value.visitingAddress.city,
                    postalCode: value.visitingAddress.postalCode,
                    stateId: value.visitingAddress.state.key,
                  },
                  administrativeName: value.postalAddress.companyName,
                  administrativeAddress: {
                    addressLine1: value.postalAddress.addressLine1,
                    addressLine2: value.postalAddress.addressLine2,
                    city: value.postalAddress.city,
                    postalCode: value.postalAddress.postalCode,
                    stateId: value.postalAddress.state.key,
                  }
                });

                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Company.Phone}>
        <PreviewPanelInPageEditableField
          field={{
            key: "phone",
            render: () => <PhoneRow numbers={convertToPhoneNumbers(companyFormState.phone)} />,
            isEmpty: !hasLandlineNumbers,
            inPageEdit: {
              fullWidth: true,
              render: ({ value, onChange, submit, cancel }) => (
                <PhoneInput
                  focusOnMount
                  value={value}
                  onChange={onChange}
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              initialValue: companyFormState.phone,
              submitChange: (value: string) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, {
                  phoneNumbers: convertToPhoneNumbers(value),
                });

                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.GeneralLabels.Website}>
        <PreviewPanelInPageEditableField
          field={{
            key: "website",
            render: () => <WebsiteLink website={companyFormState.website} />,
            isEmpty: !companyFormState.website,
            inPageEdit: {
              render: ({ value, onChange, cancel, submit }) => (
                <UrlInput
                  focusOnMount
                  value={value}
                  onValueChange={onChange}
                  validatorKey="companyWebsite"
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              initialValue: companyFormState.website,
              fullWidth: true,
              submitChange: (value: string) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, {
                  website: value,
                });

                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop="Facebook">
        <PreviewPanelInPageEditableField
          field={{
            key: "facebook",
            render: () => <WebsiteLink website={companyFormState.facebook} />,
            isEmpty: !companyFormState.facebook,
            inPageEdit: {
              render: ({ value, onChange, cancel, submit }) => (
                <UrlInput
                  focusOnMount
                  value={value}
                  onValueChange={onChange}
                  validatorKey="companyFacebook"
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              initialValue: companyFormState.facebook,
              fullWidth: true,
              submitChange: (value: string) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, {
                  facebook: value,
                });

                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      {countryFormDefinition.euVatNumber.display && <PreviewPanelDetailRow
        labelTop={countryFormDefinition.euVatNumber.label}
        subLabelTop={countryFormDefinition.euVatNumber.sublabel}
      >
        <PreviewPanelInPageEditableField
          field={{
            key: "euVatNumber",
            render: () => <VatNumberDetails companyId={props.company.companyId} euVatNumber={props.company.euVatNumber} />,
            inPageEdit: {
              fullWidth: true,
              initialValue: companyFormState.euVatNumber,
              useCustomSavingState: true,
              render: ({ value, onChange, submit, cancel, isSaving }) => (
                <OptionalTextInput
                  focusOnMount
                  value={value}
                  onChange={onChange}
                  maxLength={serverReference.constants.company.maxEuVatNumberLength}
                  onEnter={submit}
                  onEsc={cancel}
                  loading={isSaving}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: string) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, { euVatNumber: value });
                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            },
          }}
        />
      </PreviewPanelDetailRow>}

      {countryFormDefinition.fiscalNumber1.display && <PreviewPanelDetailRow
        labelTop={countryFormDefinition.fiscalNumber1.label}
        subLabelTop={countryFormDefinition.fiscalNumber1.sublabel}
      >
        <PreviewPanelInPageEditableField
          field={{
            key: "fiscalNumber1",
            render: () => <div>{companyFormState.fiscalNumber1}</div>,
            isEmpty: isNullOrEmptyString(companyFormState.fiscalNumber1),
            inPageEdit: {
              fullWidth: true,
              initialValue: companyFormState.fiscalNumber1,
              render: ({ value, onChange, submit, cancel }) => (
                <OptionalTextInput
                  focusOnMount
                  value={value}
                  onChange={onChange}
                  maxLength={serverReference.constants.company.maxFiscalNumber1Length}
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: string) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, { fiscalNumber1: value });
                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            },
          }}
        />
      </PreviewPanelDetailRow>}

      {countryFormDefinition.fiscalNumber2.display && <PreviewPanelDetailRow
        labelTop={countryFormDefinition.fiscalNumber2.label}
        subLabelTop={countryFormDefinition.fiscalNumber2.sublabel}
      >
        <PreviewPanelInPageEditableField
          field={{
            key: "fiscalNumber2",
            render: () => <div>{companyFormState.fiscalNumber2}</div>,
            isEmpty: isNullOrEmptyString(companyFormState.fiscalNumber2),
            inPageEdit: {
              fullWidth: true,
              initialValue: companyFormState.fiscalNumber2,
              render: ({ value, onChange, submit, cancel }) => (
                <OptionalTextInput
                  focusOnMount
                  value={value}
                  onChange={onChange}
                  maxLength={serverReference.constants.company.maxFiscalNumber2Length}
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: string) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, { fiscalNumber2: value });
                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>}

      {countryFormDefinition.businessRegistrationNumber.display && <PreviewPanelDetailRow
        labelTop={countryFormDefinition.businessRegistrationNumber.label}
        subLabelTop={countryFormDefinition.businessRegistrationNumber.sublabel}
      >
        <PreviewPanelInPageEditableField
          field={{
            key: "businessRegistrationNumber",
            render: () => <div>{companyFormState.businessRegistrationNumber}</div>,
            isEmpty: isNullOrEmptyString(companyFormState.businessRegistrationNumber),
            inPageEdit: {
              fullWidth: true,
              initialValue: companyFormState.businessRegistrationNumber,
              render: ({ value, onChange, submit, cancel }) => (
                <OptionalTextInput
                  focusOnMount
                  value={value}
                  onChange={onChange}
                  maxLength={serverReference.constants.company.maxBusinessRegistrationNumberLength}
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: string) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, { businessRegistrationNumber: value });
                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            },
          }}
        />
      </PreviewPanelDetailRow>}

      <PreviewPanelDetailRow labelTop={RESX.Company.CustomerNumber}>
        <PreviewPanelInPageEditableField
          field={{
            key: "internalId",
            render: () => <div>{companyFormState.internalId}</div>,
            isEmpty: isNullOrEmptyString(companyFormState.internalId),
            inPageEdit: {
              fullWidth: true,
              initialValue: companyFormState.internalId,
              render: ({ value, onChange, submit, cancel }) => (
                <OptionalTextInput
                  focusOnMount
                  value={value}
                  onChange={onChange}
                  maxLength={serverReference.constants.company.maxInternalIdLength}
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: string) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, { internalId: value });
                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            },
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Promoter.FinancialRisk}>
        <PreviewPanelInPageEditableField
          field={{
            key: "financialRisk",
            render: () => <FinancialRiskCard financialRisk={companyFormState.financialRisk} />,
            isEmpty: companyFormState.financialRisk.level.value === "unknown",
            inPageEdit: {
              fullWidth: true,
              initialValue: companyFormState.financialRisk,
              render: ({ value, onChange }) => (
                <FinancialRiskEdit
                  value={value}
                  onChange={onChange}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: server.dto.FinancialRisk) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, {
                  financialRisk: {
                    comment: value.level.value === "unknown" ? null : value.comment,
                    level: value.level.value
                  }
                });

                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Status.resxStatus}>
        <PreviewPanelInPageEditableField
          field={{
            key: "status",
            render: () => <Status status={companyFormState.status} />,
            hidden: !hasMultipleStatuses,
            inPageEdit: {
              render: ({ value, onChange }) => (
                <StatusDropdown
                  value={value}
                  onChange={onChange}
                  mainArea="company"
                  options={undefined}
                  config={{
                    focusOnMount: true,
                    openMenuOnFocus: true
                  }}
                />
              ),
              type: "confirmOrCancelButton",
              initialValue: companyFormState.status,
              fullWidth: true,
              submitChange: (value: server.dto.StatusSmall) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, {
                  statusId: value.statusId,
                });

                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.GeneralLabels.Owner}>
        <PreviewPanelInPageEditableField
          field={{
            key: "owner",
            render: () => <UserWithAvatar user={companyFormState.owner} />,
            isEmpty: !companyFormState.owner,
            inPageEdit: {
              render: ({ onChange }) => (
                <div className="main-section-template-fields-padding">
                  <UserModalPicker
                    value={undefined}
                    onChange={v => onChange(v, true)}
                    required={false}
                    validatorKey="companyOwner"
                    canCreateNew={false}
                  />
                </div>
              ),
              type: "custom",
              initialValue: companyFormState.owner,
              submitChange: (value: server.dto.UserSmall) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, {
                  ownerUserId: value.userId,
                });

                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      {hasAnyTags && <PreviewPanelDetailRow labelTop={RESX.Tag.Tags}>
        <PreviewPanelInPageEditableField
          field={{
            key: "tags",
            isEmpty: tags.length === 0,
            render: () => <TagList tags={tags} length="short" />,
            inPageEdit: {
              render: ({ value, onChange }) => (
                <div className="main-section-template-fields-padding">
                  <TagsSelect
                    mainArea="company"
                    value={value}
                    onChange={onChange}
                  />
                </div>
              ),
              type: "confirmOrCancelButton",
              initialValue: tags,
              submitChange: (tags: server.dto.TagSmall[]) => {
                return window.CoreApi.Company.UpdateTags({
                  itemId: props.company.companyId,
                  tagsIds: tags.map(tag => tag.tagId),
                });
              }
            }
          }}
        />
      </PreviewPanelDetailRow>}

      {props.viewOnMap && <>
        <ViewOnMap height={400}>
          <CompanyGoogleMap company={props.company} />
        </ViewOnMap>
      </>}
    </div>
  </PreviewPanelSectionTemplate>
}